import { useState } from 'react';
import { FormSpy } from 'react-final-form';
import { useDispatch } from 'react-redux';
import type { DocumentUpsertModel } from 'api/models';
import { StepFormLayout } from 'accounts/components/step-form-layout';
import { HOME_LICENSES_STRINGS } from 'accounts/constants/strings/home-licenses.strings';
import { useSetupFlowStep } from 'accounts/hooks/homeowners/use-setup-flow-step-context';
import { DateInputFinalFormAdapter } from 'components/DateInput/DateInput';
import Field from 'components/Field/Field';
import { InputFinalFormAdapter } from 'components/Input/Input';
import { maskMMDDYYYY, unmaskMMDDYYYY } from 'components/Input/masks';
import {
  MEDIA_LABEL,
  NewMediaUploadAdapter,
  validateMedia,
} from 'components/NewMediaUploader/NewMediaUploader';
import {
  SELECTOR_TYPES,
  SelectorFinalFormAdapter,
} from 'components/Selector/Selector';
import { Col, Row } from 'forkedlibraries/react-bootstrap';
import StandardFieldArray from 'formcomponents/StandardFieldArray/StandardFieldArray';
import FormLayout from 'layouts/FormLayout/FormLayout';
import { IMAGE_FILE_TYPE } from 'models/enums/index';
import { uploadFiles } from 'store/redux/images/actions';
import {
  composeValidators,
  dateValidationMMDDYY,
  required,
  validPastYYYYString,
} from 'utils/validation';
import {
  EXEMPTION_REASON,
  hasRentAdjustmentData,
  isCertificateOfOccupancyRequired,
  isPre1986,
  LicensesFormValues,
  MIN_RENT_HISTORY_ROWS,
  RAD_STATUS,
  toLicensesFormValues,
  toLicensesUpdatePayload,
} from './home-licenses-dc.utils';
import { RentAdjustmentFields } from './rent-adjustment-fields';
import clsx from 'clsx';

/**
 * The step's two blockers. Both are form-level errors rather than field-level ones:
 * they live under `keys` (not real field paths) so no input gets highlighted, and
 * `customError` promotes the message into the submit toast.
 *
 * - An unregistered home can't be listed, so the step must not advance — the red
 *   warning box under the selector is the on-page explanation.
 * - A rent-stabilized home has to disclose at least `MIN_RENT_HISTORY_ROWS`
 *   adjustments. Blank rows don't count towards it; a row that has been started is
 *   completed under the per-field `required` validators in `RentAdjustmentFields`.
 */
function validateLicenses(values: Record<string, unknown>) {
  const keys: Record<string, string> = {};

  if (values?.radStatus === RAD_STATUS.NONE) {
    keys.radStatus = HOME_LICENSES_STRINGS['rad.none_error'];
  }

  const rentHistory = Array.isArray(values?.rentHistory)
    ? values.rentHistory
    : [];

  if (
    values?.radStatus === RAD_STATUS.STABILIZED &&
    rentHistory.filter(hasRentAdjustmentData).length < MIN_RENT_HISTORY_ROWS
  ) {
    keys.rentHistory = HOME_LICENSES_STRINGS['rent_history.min_rows_error'];
  }

  return Object.keys(keys).length ? { keys } : {};
}

/**
 * "Registered | Rent Stabilized" fits on one line until the selected state bolds it
 * and reflows it, shifting the whole row on click. From `lg` up both lines are
 * rendered in every state; narrower screens keep the label inline. The other two
 * options are short enough to stay on one line either way.
 */
function StabilizedLabel() {
  return (
    <span className="lg:flex lg:flex-col">
      <span>{HOME_LICENSES_STRINGS['rad.stabilized_line_1']}</span>{' '}
      <span>{HOME_LICENSES_STRINGS['rad.stabilized_line_2']}</span>
    </span>
  );
}

export function HomeLicensesDC() {
  const dispatch = useDispatch();
  const { currentStepFormData, goPreviousStep, handleSaveAndNext, setLoading } =
    useSetupFlowStep()!;

  // The progress bar is rendered by `StepFormLayout` above the form, so the RAD
  // status has to be mirrored outside of it to gate forward navigation. Setting the
  // same value is a no-op in React, so this costs no extra render per keystroke.
  const [radStatus, setRadStatus] = useState<string | undefined>();

  // The backend's own 2+-unit signal, rather than deriving it from the property
  // type client-side (which is not yet settled on first render).
  const certificateOfOccupancyRequired =
    isCertificateOfOccupancyRequired(currentStepFormData);

  // `uploadFiles` is a redux thunk; the loosely-typed `dispatch` widens its
  // return to the thunk itself, so cast to the resolved upload descriptors.
  async function uploadMedia(fileType: string, media: unknown) {
    const result = (await dispatch(
      uploadFiles(fileType, media as Record<string, unknown>),
    )) as unknown as DocumentUpsertModel[] | undefined;
    return result?.length ? result[0] : undefined;
  }

  async function handleSubmit(values: LicensesFormValues) {
    const isRegistered =
      values.radStatus === RAD_STATUS.EXEMPT ||
      values.radStatus === RAD_STATUS.STABILIZED;

    let radForm1File: DocumentUpsertModel | undefined;
    let basicBusinessLicenseFile: DocumentUpsertModel | undefined;
    let certificateOfOccupancyFile: DocumentUpsertModel | undefined;

    setLoading?.(true);
    try {
      if (isRegistered && values.radForm1File) {
        radForm1File = await uploadMedia(
          IMAGE_FILE_TYPE.LicenseRadForm1DC,
          values.radForm1File,
        );
      }

      if (values.basicBusinessLicense?.file) {
        basicBusinessLicenseFile = await uploadMedia(
          IMAGE_FILE_TYPE.BasicBusinessLicenseDC,
          values.basicBusinessLicense.file,
        );
      }

      if (
        certificateOfOccupancyRequired &&
        values.certificateOfOccupancy?.file
      ) {
        certificateOfOccupancyFile = await uploadMedia(
          IMAGE_FILE_TYPE.LicenseCertificateOfOccupancyDC,
          values.certificateOfOccupancy.file,
        );
      }
    } catch (err) {
      console.error(err);
      setLoading?.(false);
      return;
    }
    setLoading?.(false);

    await handleSaveAndNext?.(
      toLicensesUpdatePayload(values, {
        certificateOfOccupancyRequired,
        radForm1File,
        basicBusinessLicenseFile,
        certificateOfOccupancyFile,
      }),
    );
  }

  return (
    <StepFormLayout
      onSubmit={handleSubmit}
      handleBackStep={goPreviousStep}
      title={HOME_LICENSES_STRINGS.title}
      subTitle={HOME_LICENSES_STRINGS.subtitle}
      initialValues={toLicensesFormValues(currentStepFormData)}
      customValidation={validateLicenses}
      // The two blockers in `validateLicenses` are mutually exclusive — the rent
      // history is only asked for once the home is registered as rent stabilized —
      // so point `customError` at whichever one can currently fire.
      customError={radStatus === RAD_STATUS.NONE ? 'radStatus' : 'rentHistory'}
      blockForwardProgress={radStatus === RAD_STATUS.NONE}
      getForm={({ values, form }: any) => (
        <>
          <FormSpy
            subscription={{ values: true }}
            onChange={({ values: formValues }) =>
              setRadStatus(formValues?.radStatus)
            }
          />

          {/* Year built — prefilled from the property; gates the DC Water
              section below. */}
          <FormLayout.Section
            firstSection
            sectionTitle={HOME_LICENSES_STRINGS['year_built.title']}
            sectionSubTitle={HOME_LICENSES_STRINGS['year_built.subtitle']}
          >
            <Row>
              <Col xs={24} md={6}>
                <Field
                  name="yearBuilt"
                  component={InputFinalFormAdapter}
                  type="number"
                  placeholder={HOME_LICENSES_STRINGS['year_built.label']}
                  validate={composeValidators(required, validPastYYYYString)}
                />
              </Col>
            </Row>
          </FormLayout.Section>

          {/* RAD registration status */}
          <FormLayout.Section
            sectionTitle={HOME_LICENSES_STRINGS['rad.title']}
            sectionSubTitle={HOME_LICENSES_STRINGS['rad.subtitle']}
          >
            <Field
              name="radStatus"
              component={SelectorFinalFormAdapter}
              validate={required}
              fluid
              type={SELECTOR_TYPES.MEDIUMTEXTBUTTON}
              tagClassName="left-0 right-0 w-fit -top-2sm mx-auto my-0"
              className="mt-sm"
              buttons={[
                {
                  label: HOME_LICENSES_STRINGS['rad.exempt_label'],
                  key: RAD_STATUS.EXEMPT,
                  tag: HOME_LICENSES_STRINGS['rad.most_common'],
                  centerText: true,
                },
                {
                  label: <StabilizedLabel />,
                  key: RAD_STATUS.STABILIZED,
                  centerText: true,
                },
                {
                  label: HOME_LICENSES_STRINGS['rad.none_label'],
                  key: RAD_STATUS.NONE,
                  centerText: true,
                },
              ]}
            />

            {/* Exempt — exemption number + reason + RAD Form 1 upload */}
            {values?.radStatus === RAD_STATUS.EXEMPT && (
              <>
                <div className="mb-2xl">
                  <Row>
                    <Col xs={24} md={6}>
                      <Field
                        name="radExemptionNumber"
                        component={InputFinalFormAdapter}
                        placeholder={
                          HOME_LICENSES_STRINGS['rad.exemption_number_label']
                        }
                        validate={required}
                      />
                    </Col>
                  </Row>
                </div>
                <FormLayout.Section
                  sectionTitle={
                    HOME_LICENSES_STRINGS['rad.exemption_reason_label']
                  }
                  className="mb-xl"
                >
                  <Field
                    name="exemptionReason"
                    component={SelectorFinalFormAdapter}
                    validate={required}
                    type={SELECTOR_TYPES.MEDIUMTEXTBUTTON}
                    className="grid grid-cols-1 gap-sm"
                    buttonClassName="min-h-[110px] w-full py-sm mb-0"
                    buttons={[
                      {
                        label:
                          HOME_LICENSES_STRINGS[
                            'rad.exemption_small_landlord_label'
                          ],
                        subLabel:
                          HOME_LICENSES_STRINGS[
                            'rad.exemption_small_landlord_sublabel'
                          ],
                        key: EXEMPTION_REASON.SMALL_LANDLORD,
                        centerText: true,
                      },
                      {
                        label:
                          HOME_LICENSES_STRINGS[
                            'rad.exemption_new_construction_label'
                          ],
                        subLabel:
                          HOME_LICENSES_STRINGS[
                            'rad.exemption_new_construction_sublabel'
                          ],
                        key: EXEMPTION_REASON.NEW_CONSTRUCTION,
                        centerText: true,
                      },
                      {
                        label:
                          HOME_LICENSES_STRINGS[
                            'rad.exemption_government_label'
                          ],
                        subLabel:
                          HOME_LICENSES_STRINGS[
                            'rad.exemption_government_sublabel'
                          ],
                        key: EXEMPTION_REASON.GOVERNMENT,
                        centerText: true,
                      },
                      {
                        label:
                          HOME_LICENSES_STRINGS[
                            'rad.exemption_cooperative_label'
                          ],
                        subLabel:
                          HOME_LICENSES_STRINGS[
                            'rad.exemption_cooperative_sublabel'
                          ],
                        key: EXEMPTION_REASON.COOPERATIVE,
                        centerText: true,
                      },
                      {
                        label:
                          HOME_LICENSES_STRINGS['rad.exemption_other_label'],
                        subLabel:
                          HOME_LICENSES_STRINGS['rad.exemption_other_sublabel'],
                        key: EXEMPTION_REASON.OTHER,
                        centerText: true,
                      },
                    ]}
                  />
                </FormLayout.Section>

                <div className="mt-0">
                  <Field
                    name="radForm1File"
                    validate={validateMedia}
                    component={NewMediaUploadAdapter}
                    mediaLabel={MEDIA_LABEL.FILE_UPLOAD}
                    customLabel={HOME_LICENSES_STRINGS['rad.form_upload_label']}
                    disableWebcam
                    cleanOnMediaChange
                    isSingleMedia
                    ignoreError={false}
                    size="fluid"
                    fieldWrapperClassName="!pt-0"
                  />
                </div>
              </>
            )}

            {/* Rent stabilized — registration number + info + RAD Form 1 upload */}
            {values?.radStatus === RAD_STATUS.STABILIZED && (
              <>
                <div className="mb-xl">
                  <Row>
                    <Col xs={24} md={6}>
                      <Field
                        name="radRegistrationNumber"
                        component={InputFinalFormAdapter}
                        placeholder={
                          HOME_LICENSES_STRINGS['rad.registration_number_label']
                        }
                        validate={required}
                      />
                    </Col>
                  </Row>
                </div>

                <div className="mt-0 rounded border border-solid border-gray p-sm text-body bg-light-gray text-navy">
                  {HOME_LICENSES_STRINGS['rad.stabilized_info']}
                </div>
                <Field
                  name="radForm1File"
                  validate={validateMedia}
                  component={NewMediaUploadAdapter}
                  mediaLabel={MEDIA_LABEL.FILE_UPLOAD}
                  customLabel={HOME_LICENSES_STRINGS['rad.form_upload_label']}
                  disableWebcam
                  cleanOnMediaChange
                  isSingleMedia
                  ignoreError={false}
                  size="fluid"
                />
              </>
            )}

            {/* Not yet registered — a hard stop. `validateRadRegistration` keeps the
                step from advancing, so nothing is saved until the home is registered. */}
            {values?.radStatus === RAD_STATUS.NONE && (
              <div className="mt-lg rounded border border-solid border-red bg-red-translucent-light p-sm">
                {HOME_LICENSES_STRINGS['rad.none_warning']}
              </div>
            )}
          </FormLayout.Section>

          {/* Basic Business License */}
          <FormLayout.Section
            separatorType="normal"
            sectionTitle={HOME_LICENSES_STRINGS['bbl.title']}
            sectionSubTitle={HOME_LICENSES_STRINGS['bbl.subtitle']}
          >
            <Row>
              <Col xs={24} md={6}>
                <Field
                  name="basicBusinessLicense.number"
                  component={InputFinalFormAdapter}
                  placeholder={HOME_LICENSES_STRINGS['bbl.number_label']}
                  validate={required}
                />
              </Col>
              <Col xs={24} md={6}>
                <Field
                  name="basicBusinessLicense.nameOnLicense"
                  component={InputFinalFormAdapter}
                  placeholder={HOME_LICENSES_STRINGS['bbl.name_label']}
                  validate={required}
                />
              </Col>
              <Col xs={24} md={6}>
                <Field
                  name="basicBusinessLicense.expirationDate"
                  component={DateInputFinalFormAdapter}
                  mask={maskMMDDYYYY}
                  unmask={unmaskMMDDYYYY}
                  placeholder={HOME_LICENSES_STRINGS['bbl.expiration_label']}
                  validate={composeValidators(required, dateValidationMMDDYY)}
                />
              </Col>
            </Row>
            <div
              className={clsx(
                'mt-lg',
                !certificateOfOccupancyRequired &&
                  !isPre1986(values?.yearBuilt) &&
                  values?.radStatus !== RAD_STATUS.STABILIZED
                  ? 'pb-xs'
                  : '',
              )}
            >
              <Field
                name="basicBusinessLicense.file"
                validate={validateMedia}
                component={NewMediaUploadAdapter}
                mediaLabel={MEDIA_LABEL.FILE_UPLOAD}
                customLabel={HOME_LICENSES_STRINGS['bbl.upload_label']}
                disableWebcam
                cleanOnMediaChange
                isSingleMedia
                ignoreError={false}
                size="fluid"
              />
            </div>
          </FormLayout.Section>

          {/* Certificate of Occupancy — 2+ unit properties only */}
          {certificateOfOccupancyRequired && (
            <FormLayout.Section
              separatorType="normal"
              sectionTitle={HOME_LICENSES_STRINGS['cofo.title']}
              sectionSubTitle={HOME_LICENSES_STRINGS['cofo.subtitle']}
            >
              <Row>
                <Col xs={24} md={12}>
                  <Field
                    name="certificateOfOccupancy.number"
                    component={InputFinalFormAdapter}
                    placeholder={
                      HOME_LICENSES_STRINGS['cofo.number_placeholder']
                    }
                    validate={required}
                  />
                </Col>
                <Col xs={24} md={12}>
                  <Field
                    name="certificateOfOccupancy.issuanceDate"
                    component={DateInputFinalFormAdapter}
                    mask={maskMMDDYYYY}
                    unmask={unmaskMMDDYYYY}
                    placeholder={HOME_LICENSES_STRINGS['cofo.issuance_label']}
                    validate={composeValidators(required, dateValidationMMDDYY)}
                  />
                </Col>
              </Row>
              <div className="mt-lg">
                <Field
                  name="certificateOfOccupancy.file"
                  validate={validateMedia}
                  component={NewMediaUploadAdapter}
                  mediaLabel={MEDIA_LABEL.FILE_UPLOAD}
                  customLabel={HOME_LICENSES_STRINGS['cofo.upload_label']}
                  disableWebcam
                  cleanOnMediaChange
                  isSingleMedia
                  ignoreError={false}
                  size="fluid"
                />
              </div>
            </FormLayout.Section>
          )}

          {/* DC Water lead service line — homes built before 1986 only */}
          {isPre1986(values?.yearBuilt) && (
            <div
              className={clsx(
                'mt-2xl',
                values?.radStatus !== RAD_STATUS.STABILIZED && 'mb-2xl',
              )}
            >
              <FormLayout.Section
                separatorType="normal"
                sectionTitle={HOME_LICENSES_STRINGS['dc_water.title']}
                sectionSubTitle={HOME_LICENSES_STRINGS['dc_water.subtitle']}
              >
                <Row>
                  <Col xs={24} md={12}>
                    <Field
                      name="dcWaterLeadMap.privateType"
                      component={InputFinalFormAdapter}
                      placeholder={
                        HOME_LICENSES_STRINGS['dc_water.private_type_label']
                      }
                      validate={required}
                    />
                  </Col>
                  <Col xs={24} md={12}>
                    <Field
                      name="dcWaterLeadMap.privateDescription"
                      component={InputFinalFormAdapter}
                      placeholder={
                        HOME_LICENSES_STRINGS[
                          'dc_water.private_description_label'
                        ]
                      }
                      validate={required}
                    />
                  </Col>
                  <Col xs={24} md={12}>
                    <Field
                      name="dcWaterLeadMap.publicType"
                      component={InputFinalFormAdapter}
                      placeholder={
                        HOME_LICENSES_STRINGS['dc_water.public_type_label']
                      }
                      validate={required}
                    />
                  </Col>
                  <Col xs={24} md={12}>
                    <Field
                      name="dcWaterLeadMap.publicDescription"
                      component={InputFinalFormAdapter}
                      placeholder={
                        HOME_LICENSES_STRINGS[
                          'dc_water.public_description_label'
                        ]
                      }
                      validate={required}
                    />
                  </Col>
                </Row>
              </FormLayout.Section>
            </div>
          )}

          {/* Rent history — rent-stabilized homes only */}
          {values?.radStatus === RAD_STATUS.STABILIZED && (
            <div className="mt-2xl">
              <FormLayout.Section
                sectionTitle={HOME_LICENSES_STRINGS['rent_history.title']}
                sectionSubTitle={HOME_LICENSES_STRINGS['rent_history.subtitle']}
              >
                <StandardFieldArray
                  fieldArrayName="rentHistory"
                  mutators={form.mutators}
                  FieldsComponent={RentAdjustmentFields}
                  values={form.getState().values}
                  form={form}
                  singleLine={false}
                  showTrashButton
                  iconButtonProps={{
                    label: HOME_LICENSES_STRINGS['rent_history.add_row'],
                  }}
                />
              </FormLayout.Section>
            </div>
          )}
        </>
      )}
    />
  );
}
